import { ref } from 'vue';

const escapeHtml = (value) => `${value}`
	.replace(/&/g, '&amp;')
	.replace(/</g, '&lt;')
	.replace(/>/g, '&gt;')
	.replace(/"/g, '&quot;')
	.replace(/'/g, '&#039;');

/**
 * Turn a validation key into a readable label, e.g.
 * "job_preferences.days.tuesday" => "Job Preferences - Days - Tuesday".
 */
export const formatValidationAttribute = (attribute) => `${attribute}`
	.split('.')
	.map((segment) => segment.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
	.join(' - ');

/**
 * Build an HTML toast message from a heading plus every message in the error response,
 * one per line, with raw validation keys swapped for readable labels.
 */
export const formatErrorMessage = (heading, error) => {
	const data = error?.response?.data ?? {};
	let messages = [];

	if (data.errors && typeof data.errors === 'object') {
		messages = Object.entries(data.errors).flatMap(([attribute, fieldMessages]) => {
			const label = formatValidationAttribute(attribute);

			return [].concat(fieldMessages).map((message) => {
				const readable = `${message}`
					.split(attribute).join(label)
					.split(attribute.replace(/_/g, ' ')).join(label);

				return readable.startsWith(`The ${label}`) ? readable.slice(4) : readable;
			});
		});
	} else if (data.message) {
		messages = [data.message];
	}

	return [heading, ...messages].map(escapeHtml).join('<br>');
};

export function useValidation() {
	const validationErrors = ref({});

	const clearErrors = () => {
		validationErrors.value = {};
	};

	const getValidationClass = (field) => {
		return validationErrors.value[field] ? 'is-invalid' : undefined;
	};

	const getInvalidFeedback = (field) => {
		return validationErrors.value[field] ? validationErrors.value[field].join(' ') : '';
	};

	const getValidationState = (field) => {
		if (!Array.isArray(validationErrors.value[field])) {
			return null;
		}

		return !validationErrors.value[field];
	};

	const hasError = (field) => {
		return !!validationErrors.value[field];
	};

	const getValidationErrors = (field, htmlLineBreak = false) => {
		if (!Array.isArray(validationErrors.value[field])) {
			return null;
		}

		return validationErrors.value[field].length === 1
			? validationErrors.value[field][0]
			: validationErrors.value[field].join(htmlLineBreak ? '<br>' : '\n');
	};

	const handleError = (error) => {
		if (!error.response || !error.request) {
			throw error;
		}

		if (error.response.status === 422) {
			clearErrors();
			validationErrors.value = error.response.data.errors;
		}

		if ([400, 500].indexOf(error.response.status) > -1 && error.response.data.message) {
			toastr.error(error.response.data.message);
		}

		if (error.response.status === 404) {
			toastr.error('We couldn\'t find the required resource');
		}
	};

	// Stored as an array, the same shape as a server validation response
	const setValidationError = (field, message) => {
		validationErrors.value[field] = [`${message}`];
	};

	const removeValidationError = (field) => {
		validationErrors.value[field] = undefined;
	};

	return {
		validationErrors,
		clearErrors,
		getValidationClass,
		getInvalidFeedback,
		getValidationState,
		hasError,
		getValidationErrors,
		handleError,
		setValidationError,
		removeValidationError,
		formatErrorMessage,
	};
}
